
import React from 'react';
import { ShoppingCart, Plus, Check, Star, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Product } from '../types';
import { useStores } from '../contexts/StoreContext';
import { OptimizedImage } from './OptimizedImage';

interface ProductCardProps {
    product: Product;
    isDark: boolean;
    isRow?: boolean;
    onOpenDetails: (product: Product) => void;
    onAddToCart: (e: React.MouseEvent, product: Product) => void;
    isAdded: boolean;
    priority?: boolean;
}

export const ProductCard: React.FC<ProductCardProps> = ({
    product,
    isDark,
    isRow = false,
    onOpenDetails,
    onAddToCart,
    isAdded,
    priority = false
}) => {
    const { stores } = useStores();
    const navigate = useNavigate();

    // Find store to get the global productCardBanner
    const productStore = product.storeId ? stores.find(s => s.id.toString() === product.storeId.toString()) : null;

    // Use global store banner if available, otherwise fallback to product-specific banner
    const displayBanner = productStore?.productCardBanner || product.storeBanner;

    const handleStoreClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (productStore?.id) {
            navigate(`/shop/${productStore.id}`);
        } else if (product.storeId) {
            navigate(`/shop/${product.storeId}`);
        }
    };

    return (
        <div
            data-category={product.categoryId}
            onClick={() => onOpenDetails(product)}
            className={`
        group relative rounded-[2rem] p-3 md:p-4 transition-transform duration-300 ease-out hover:scale-[1.02] cursor-pointer border flex flex-col select-none
        ${isDark ? 'bg-[#1a1c1e] border-white/5' : 'bg-white border-slate-100 shadow-sm'}
        ${isRow ? 'min-w-[160px] w-[160px] md:min-w-[220px] md:w-[220px] snap-center h-full' : 'h-full w-full'}
        gpu-accel
        `}
        >
            {/* Image Container with Badge */}
            <div className={`relative w-full aspect-square mb-3 rounded-[1.5rem] overflow-hidden shrink-0`}>
                <OptimizedImage
                    src={product.images && product.images.length > 0 ? product.images.find(img => img.isMain)?.url || product.images[0].url : product.image}
                    alt={product.name}
                    aspectRatio="1/1"
                    priority={priority}
                    className="w-full h-full transition-transform duration-300 ease-out group-hover:scale-110 select-none pointer-events-auto"
                    containerBackground={isDark
                        ? 'radial-gradient(circle at center, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)'
                        : 'radial-gradient(circle at center, #f8fafc 0%, #f1f5f9 100%)'}
                />
                {/* Discount Badge - Absolute top */}
                {product.discount && (
                    <div className="absolute top-2 right-2 bg-[#d1fae5]/90 backdrop-blur-sm text-[#059669] text-[10px] md:text-xs font-black px-2 py-1 rounded-lg shadow-sm z-10">
                        {product.discount}%
                    </div>
                )}

                {/* Gift Points Badge - Absolute top left */}
                {product.giftPoints && product.giftPoints > 0 ? (
                    <div className="absolute top-2 left-2 bg-gradient-to-r from-amber-500 to-yellow-500 text-white text-[9px] md:text-[10px] font-black px-1.5 py-1 rounded-lg shadow-md z-10 flex items-center gap-1 border border-amber-300/50">
                        <Sparkles size={10} className="text-amber-100 animate-pulse" />
                        <span>+{product.giftPoints} نقطة</span>
                    </div>
                ) : null}
            </div>

            {/* Product Info - Flex Grow to push footer down */}
            <div className="flex flex-col gap-1 flex-grow min-h-0">
                <h3
                    className={`font-black text-sm md:text-lg text-right leading-tight line-clamp-1 ${isDark ? 'text-white' : 'text-slate-800'}`}
                    title={product.name}
                    style={{
                        display: '-webkit-box',
                        WebkitLineClamp: 1,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                    }}
                >
                    {product.name}
                </h3>

                <div className="flex items-center justify-end gap-1.5 mb-1 shrink-0">
                    {product.reviewCount && product.reviewCount > 0 ? (
                        <span className="text-[9px] md:text-[10px] font-black text-blue-500">
                            ({product.reviewCount})
                        </span>
                    ) : null}
                    <div className="flex text-yellow-400 text-[10px] md:text-xs gap-0.5">
                        {[...Array(5)].map((_, i) => (
                            <Star key={i} size={10} fill={i < Math.floor(product.rating || 5) ? "currentColor" : "none"} strokeWidth={i < Math.floor(product.rating || 5) ? 0 : 2} className={i < Math.floor(product.rating || 5) ? "" : "text-gray-300"} />
                        ))}
                    </div>
                </div>

                <p className={`text-[10px] md:text-xs text-right mb-2 line-clamp-1 text-ellipsis overflow-hidden ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                    {product.description}
                </p>
            </div>

            {/* Store Banner Area - Fixed Height */}
            {displayBanner && (
                <div
                    className="w-full h-8 md:h-10 bg-black/5 dark:bg-white/5 rounded-lg mb-3 overflow-hidden flex items-center justify-center shrink-0 cursor-pointer"
                    onClick={handleStoreClick}
                >
                    <OptimizedImage
                        src={displayBanner}
                        alt="Store"
                        aspectRatio="4/1"
                        className="w-full h-full opacity-80 hover:opacity-100 transition-opacity select-none pointer-events-none"
                    />
                </div>
            )}

            {/* Footer: Cart & Price - Aligned to bottom using mt-auto from flex parent */}
            <div className="flex items-center justify-between mt-auto relative pt-2 border-t border-dashed border-gray-500/10 shrink-0">
                {/* Cart Button */}
                <div className="relative">
                    {isAdded && (
                        <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 z-20 animate-fade-in-up whitespace-nowrap">
                            <div className="bg-emerald-500 text-white text-[10px] font-bold px-2 py-1 rounded-md shadow-lg flex items-center gap-1">
                                <span>تمت الإضافة</span>
                                <Check size={10} strokeWidth={4} />
                            </div>
                            {/* Triangle Arrow */}
                            <div className="w-0 h-0 border-l-[4px] border-l-transparent border-r-[4px] border-r-transparent border-t-[6px] border-t-emerald-500 mx-auto"></div>
                        </div>
                    )}
                    <button
                        onClick={(e) => onAddToCart(e, product)}
                        className={`w-9 h-9 md:w-11 md:h-11 rounded-full border flex items-center justify-center transition-all duration-300 active:scale-90 group/cart relative overflow-hidden ${isDark ? 'border-red-400/30 text-red-400 hover:bg-red-900/20' : 'border-red-200 text-[#f43f5e] hover:bg-red-50'}`}
                    >
                        <div className="absolute inset-0 bg-current opacity-0 group-hover/cart:opacity-10 transition-opacity duration-300 rounded-full"></div>
                        <ShoppingCart size={18} className="md:w-5 md:h-5 transition-transform duration-300 group-hover/cart:scale-110" />
                        <Plus size={10} className="absolute top-2 right-2 opacity-0 group-hover/cart:opacity-100 transition-opacity duration-300" />
                    </button>
                </div>

                {/* Price Block */}
                <div className="flex flex-col items-end justify-center h-full">
                    {product.oldPrice && (
                        <span className={`text-[10px] md:text-xs font-bold line-through decoration-red-400/60 tabular-nums ${isDark ? 'text-slate-600' : 'text-slate-400'}`}>
                            {product.oldPrice.toLocaleString()}
                        </span>
                    )}
                    <span className={`text-sm md:text-xl font-black tabular-nums tracking-tight leading-none ${isDark ? 'text-white' : 'text-slate-800'}`}>
                        {product.price.toLocaleString()} <span className="text-[10px] md:text-xs font-bold text-slate-500">ج.م</span>
                    </span>
                </div>
            </div>
        </div>
    );
};
